import { DashboardConfig, DashboardFilters, RecentDashboard, DashboardStats } from '../types/dashboard'
import { getServiceUrl, configService } from '../config/environment'

/**
 * Dashboard service for managing dashboard data and operations
 * Now uses secure API that filters dashboards based on user roles
 */
class DashboardService {
  private dashboards: DashboardConfig[] = []
  private favorites: Set<string> = new Set()
  private recentDashboards: Map<string, RecentDashboard> = new Map()
  private lastFetched: number = 0
  private cacheTTL: number = 5 * 60 * 1000 // 5 minutes

  constructor() {
    this.loadFromLocalStorage()
  }

  /**
   * Get JWT token from localStorage for API calls
   */
  private getAuthToken(): string | null {
    // Check all LogTo keys in localStorage for the ID token
    const allLogtoKeys = Object.keys(localStorage).filter(key => key.startsWith('logto'))
    const idTokenKeys = allLogtoKeys.filter(key => key.endsWith(':idToken'))
    
    for (const key of idTokenKeys) {
      const value = localStorage.getItem(key)
      if (value && typeof value === 'string' && value.split('.').length === 3) {
        return value
      }
    }
    
    return null
  }

  /**
   * Fetch dashboards from secure API endpoint
   */
  async fetchDashboardsFromAPI(): Promise<DashboardConfig[]> {
    try {
      const token = this.getAuthToken()
      if (!token) {
        console.warn('No authentication token found - returning empty dashboard list')
        return []
      }

      // Use the JWT auth service API endpoint (correct path construction)
      const apiUrl = getServiceUrl('api', '/dashboards')
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Authentication failed - user may need to log in again')
          return []
        }
        throw new Error(`Failed to fetch dashboards: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      this.dashboards = data.dashboards || []
      this.lastFetched = Date.now()
      
      console.info(`Loaded ${this.dashboards.length} accessible dashboards from API`)
      return this.dashboards
    } catch (error) {
      console.error('Failed to fetch dashboards from API:', error)
      // Return empty array on error - no fallback to expose client information
      return []
    }
  }

  /**
   * Get all available dashboards (fetches from API if needed)
   */
  async getAllDashboards(): Promise<DashboardConfig[]> {
    // Check if we need to refresh from API
    const now = Date.now()
    if (this.dashboards.length === 0 || (now - this.lastFetched) > this.cacheTTL) {
      await this.fetchDashboardsFromAPI()
    }
    
    return [...this.dashboards]
  }

  /**
   * Get dashboard by ID
   */
  getDashboardById(id: string): DashboardConfig | undefined {
    return this.dashboards.find(dashboard => dashboard.id === id)
  }

  /**
   * Filter dashboards based on criteria
   */
  filterDashboards(filters: DashboardFilters, dashboardsToFilter?: DashboardConfig[]): DashboardConfig[] {
    let filtered = [...(dashboardsToFilter || this.dashboards)]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(dashboard =>
        dashboard.name.toLowerCase().includes(searchLower) ||
        dashboard.description.toLowerCase().includes(searchLower) ||
        dashboard.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      )
    }

    if (filters.category) {
      filtered = filtered.filter(dashboard => dashboard.category === filters.category)
    }

    if (filters.status) {
      filtered = filtered.filter(dashboard => dashboard.status === filters.status)
    }

    if (filters.tags?.length) {
      filtered = filtered.filter(dashboard =>
        filters.tags!.some(tag => dashboard.tags?.includes(tag))
      )
    }

    return filtered
  }

  /**
   * Get unique categories (from accessible dashboards only)
   */
  getCategories(): string[] {
    return [...new Set(this.dashboards.map(d => d.category).filter(Boolean))] as string[]
  }

  /**
   * Get unique tags (from accessible dashboards only)
   */
  getTags(): string[] {
    const allTags = this.dashboards.flatMap(d => d.tags || [])
    return [...new Set(allTags)]
  }

  /**
   * Add dashboard to favorites
   */
  addToFavorites(dashboardId: string): void {
    this.favorites.add(dashboardId)
    this.saveToLocalStorage()
  }

  /**
   * Remove dashboard from favorites
   */
  removeFromFavorites(dashboardId: string): void {
    this.favorites.delete(dashboardId)
    this.saveToLocalStorage()
  }

  /**
   * Check if dashboard is favorite
   */
  isFavorite(dashboardId: string): boolean {
    return this.favorites.has(dashboardId)
  }

  /**
   * Get favorite dashboards
   */
  getFavoriteDashboards(): DashboardConfig[] {
    return this.dashboards.filter(dashboard => this.favorites.has(dashboard.id))
  }

  /**
   * Record dashboard access
   */
  recordDashboardAccess(dashboardId: string): void {
    const existing = this.recentDashboards.get(dashboardId)
    const now = new Date().toISOString()

    if (existing) {
      existing.lastAccessed = now
      existing.accessCount += 1
    } else {
      this.recentDashboards.set(dashboardId, {
        id: dashboardId,
        lastAccessed: now,
        accessCount: 1
      })
    }

    // Keep only last 10 recent items
    if (this.recentDashboards.size > 10) {
      const entries = Array.from(this.recentDashboards.entries())
      entries.sort((a, b) => new Date(b[1].lastAccessed).getTime() - new Date(a[1].lastAccessed).getTime())
      
      this.recentDashboards.clear()
      entries.slice(0, 10).forEach(([id, recent]) => {
        this.recentDashboards.set(id, recent)
      })
    }

    this.saveToLocalStorage()
  }

  /**
   * Get recent dashboards
   */
  getRecentDashboards(): DashboardConfig[] {
    const recentIds = Array.from(this.recentDashboards.values())
      .sort((a, b) => new Date(b.lastAccessed).getTime() - new Date(a.lastAccessed).getTime())
      .map(r => r.id)

    return recentIds
      .map(id => this.getDashboardById(id))
      .filter(Boolean) as DashboardConfig[]
  }

  /**
   * Get dashboard statistics
   */
  getDashboardStats(accessibleDashboards: DashboardConfig[]): DashboardStats {
    return {
      totalDashboards: accessibleDashboards.length, // User only sees their accessible count
      accessibleDashboards: accessibleDashboards.length,
      favoriteCount: this.favorites.size,
      recentCount: this.recentDashboards.size
    }
  }

  /**
   * Navigate to dashboard (records access and returns URL)
   */
  navigateToDashboard(dashboardId: string): string {
    const dashboard = this.getDashboardById(dashboardId)
    if (!dashboard) {
      throw new Error(`Dashboard ${dashboardId} not found`)
    }

    this.recordDashboardAccess(dashboardId)
    
    // Return the path from the dashboard configuration
    return this.generateDashboardUrl(dashboard.path)
  }

  /**
   * Generate dashboard URL based on path
   */
  private generateDashboardUrl(path: string): string {
    try {
      // For paths that start with /, use the current origin (includes port)
      if (path.startsWith('/')) {
        // Use window.location.origin to get the correct protocol://host:port
        return `${window.location.origin}${path}`
      }
      
      // Fallback for relative paths
      return getServiceUrl('api', path)
    } catch (error) {
      console.error('Error generating dashboard URL:', error)
      // Fallback to current origin
      return `${window.location.origin}${path}`
    }
  }

  /**
   * Search dashboards with suggestions
   */
  searchDashboards(query: string, limit: number = 5, dashboardsToSearch?: DashboardConfig[]): DashboardConfig[] {
    if (!query.trim()) return []

    const queryLower = query.toLowerCase()
    const searchPool = dashboardsToSearch || this.dashboards
    const results = searchPool.filter(dashboard =>
      dashboard.name.toLowerCase().includes(queryLower) ||
      dashboard.description.toLowerCase().includes(queryLower)
    )

    return results.slice(0, limit)
  }

  /**
   * Load data from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const favoritesData = localStorage.getItem('meq_dashboard_favorites')
      if (favoritesData) {
        this.favorites = new Set(JSON.parse(favoritesData))
      }

      const recentData = localStorage.getItem('meq_dashboard_recent')
      if (recentData) {
        const recentArray = JSON.parse(recentData)
        this.recentDashboards = new Map(recentArray.map((item: RecentDashboard) => [item.id, item]))
      }
    } catch (err) {
      console.warn('Failed to load dashboard data from localStorage:', err)
    }
  }

  /**
   * Save data to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem('meq_dashboard_favorites', JSON.stringify([...this.favorites]))
      localStorage.setItem('meq_dashboard_recent', JSON.stringify([...this.recentDashboards.values()]))
    } catch (err) {
      console.warn('Failed to save dashboard data to localStorage:', err)
    }
  }

  /**
   * Initialize dashboards (fetch from secure API)
   */
  async initializeDashboards(): Promise<void> {
    await this.fetchDashboardsFromAPI()
  }

  /**
   * Get current environment configuration for debugging
   */
  getEnvironmentInfo() {
    return configService.getDebugInfo()
  }

  /**
   * Force refresh dashboards from API
   */
  async refreshDashboards(): Promise<void> {
    this.lastFetched = 0 // Force refresh
    await this.fetchDashboardsFromAPI()
  }
}

// Export singleton instance
export const dashboardService = new DashboardService()
export default dashboardService 